import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Clock, Link2, Plus, Target, Upload, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/primitives';
import { adminApi, formatDate, plural, useAdminQuery } from './adminApi';
import { AdminSection, CampaignStatusBadge, FilterChips, RefreshError, SearchField, sourceLabel } from './AdminUi';
import CampaignDetail from './CampaignDetail';
import { CreateCampaignDialog, CsvImportDialog, EditCampaignDialog, OtxImportDialog } from './CampaignDialogs';

/**
 * Hunt campaigns: master list on the left, the selected campaign on the
 * right. The selection lives in the URL (?campaign=<id>) and is kept by id
 * across reloads, filters and mutations; when the selected campaign is
 * filtered out or deleted, the first visible one is shown instead.
 */
export default function CampaignsTab({ onChanged }) {
  const { data, error, loading, reload } = useAdminQuery(
    () => adminApi.get('/admin/threat-hunt-campaigns').then((r) => r.data),
    []
  );
  const campaigns = data?.campaigns || [];
  const stats = data?.stats || {};

  const [params, setParams] = useSearchParams();
  const selectedId = params.get('campaign');
  const select = (id) => {
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (id) next.set('campaign', id);
      else next.delete('campaign');
      return next;
    }, { replace: true });
  };

  const [status, setStatus] = useState(null);
  const [q, setQ] = useState('');
  const [dialog, setDialog] = useState(null); // 'create' | 'otx' | 'csv'
  const [editing, setEditing] = useState(null);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return campaigns.filter((c) =>
      (!status || c.status === status) && (!needle || String(c.name).toLowerCase().includes(needle))
    );
  }, [campaigns, status, q]);
  const selected = filtered.find((c) => c.id === selectedId) || filtered[0] || null;

  const changed = (selectId) => {
    if (selectId) {
      select(selectId);
      // A new draft must be visible even if a status filter would hide it.
      setStatus((s) => (s && s !== 'pending' ? null : s));
    }
    reload();
    onChanged?.();
  };

  const statusOptions = [
    { value: null, label: 'All', count: stats.total ?? campaigns.length },
    { value: 'pending', label: 'Pending review', count: stats.pending ?? 0, icon: Clock },
    { value: 'approved', label: 'Live', count: stats.approved ?? 0, icon: CheckCircle2 },
    { value: 'rejected', label: 'Rejected', count: stats.rejected ?? 0, icon: XCircle },
  ];

  return (
    <AdminSection
      title="Hunt campaigns"
      count={data ? campaigns.length : null}
      description="Curated IOC sets. Drafts are invisible to customers until you publish them."
      actions={
        <>
          <Button variant="outline" size="sm" onClick={() => setDialog('otx')}>
            <Link2 aria-hidden="true" /> Import OTX pulse
          </Button>
          <Button variant="outline" size="sm" onClick={() => setDialog('csv')}>
            <Upload aria-hidden="true" /> Import CSV
          </Button>
          <Button size="sm" onClick={() => setDialog('create')} data-testid="create-campaign-btn">
            <Plus aria-hidden="true" /> New campaign
          </Button>
        </>
      }
    >
      {loading ? (
        <LoadingState label="campaigns" rows={5} />
      ) : error && !data ? (
        <ErrorState title="Couldn't load campaigns" error={error} onRetry={reload} />
      ) : !campaigns.length ? (
        <EmptyState
          icon={Target}
          title="No hunt campaigns yet"
          description="Create one, import an OTX pulse or CSV, or create one from a brief on the Detection rules tab."
          action={<Button size="sm" onClick={() => setDialog('create')}><Plus aria-hidden="true" /> New campaign</Button>}
        />
      ) : (
        <>
          <RefreshError error={error} onRetry={reload} what="campaigns" />
          <div className="adm-master-detail">
            <div className="adm-master">
              <div className="adm-master-tools">
                <SearchField value={q} onChange={setQ} label="Search campaigns" placeholder="Search campaign names" />
                <FilterChips label="Campaign status" options={statusOptions} value={status} onChange={setStatus} testIdPrefix="campaign-filter" />
              </div>
              {!filtered.length ? (
                <EmptyState variant="compact" title="No matching campaigns" description="Change the search or the status filter." />
              ) : (
                <ul className="adm-master-list" aria-label="Campaigns">
                  {filtered.map((c) => {
                    const active = selected?.id === c.id;
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          className="adm-master-item"
                          aria-current={active ? 'true' : undefined}
                          onClick={() => select(c.id)}
                          data-testid={`campaign-item-${c.id}`}
                        >
                          <span className="adm-master-name">{c.name}</span>
                          <span className="adm-master-meta">
                            <CampaignStatusBadge status={c.status} />
                            <span className="adm-meta">{sourceLabel(c.source_type)}</span>
                          </span>
                          <span className="adm-meta">
                            {plural(c.ioc_count || 0, 'IOC')}
                            {c.status === 'approved' && c.pending_ioc_count > 0 ? ` · ${c.pending_ioc_count} unpublished` : ''}
                            {' · '}{formatDate(c.created_at)}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="adm-detail-pane">
              {selected ? (
                <CampaignDetail
                  key={selected.id}
                  campaign={selected}
                  onChanged={() => { reload(); onChanged?.(); }}
                  onEdit={setEditing}
                  onDeleted={() => { select(null); reload(); onChanged?.(); }}
                />
              ) : (
                <EmptyState icon={Target} title="No campaign selected" description="Pick a campaign from the list." />
              )}
            </div>
          </div>
        </>
      )}

      <CreateCampaignDialog open={dialog === 'create'} onOpenChange={(o) => !o && setDialog(null)} onDone={changed} />
      <OtxImportDialog open={dialog === 'otx'} onOpenChange={(o) => !o && setDialog(null)} onDone={changed} />
      <CsvImportDialog open={dialog === 'csv'} onOpenChange={(o) => !o && setDialog(null)} onDone={changed} />
      <EditCampaignDialog campaign={editing} onOpenChange={(o) => !o && setEditing(null)} onDone={() => { reload(); }} />
    </AdminSection>
  );
}
